/* ROMANTICO - Marquee rail (decorative) on the top border of a box.
   The only job of this script is to write the motion path from the real width
   of the box, and to derive the animation duration from the real path length.
   It runs at mount and on resize, never per frame. Zero libraries. */
(function () {
  if (customElements.get('marquee-rail')) return;

  function supportsOffsetPath() {
    return (
      typeof window.CSS !== 'undefined' &&
      typeof window.CSS.supports === 'function' &&
      window.CSS.supports('offset-path', 'path("M0,0 L1,1")')
    );
  }

  function pathLength(d, fallback) {
    var node = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    node.setAttribute('d', d);
    var length = 0;
    try {
      length = node.getTotalLength();
    } catch (error) {
      length = 0;
    }
    if (!length || !isFinite(length)) length = fallback;
    return length;
  }

  class MarqueeRail extends HTMLElement {
    connectedCallback() {
      if (!supportsOffsetPath()) {
        this.remove();
        return;
      }

      if (!this.id) {
        this.id = 'MarqueeRail-' + Math.random().toString(36).slice(2, 10);
      }

      this.itemSize = parseFloat(this.dataset.itemSize) || 28;
      /* The rail is a band, not a hairline: it is BAND item sizes tall above
         and below the line, so the motion path baseline sits at that offset.
         Must stay in sync with height/top in assets/marquee-rail.css. */
      this.baseline = this.itemSize * 2;
      this.speed = parseFloat(this.dataset.speed) || 34;
      this.amplitude = parseFloat(this.dataset.amplitude) || 0;
      this.measuredLength = 0;
      this.frame = 0;

      if (!this.styleEl) {
        this.styleEl = document.createElement('style');
        this.appendChild(this.styleEl);
      }
      this.guide = this.querySelector('.marquee-rail__guide-path');
      this.host = this.parentElement || this;
      this.syncHost();

      this.handleResize = this.handleResize.bind(this);
      this.handleThemeEvent = this.handleThemeEvent.bind(this);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.host);
      }
      document.addEventListener('shopify:section:load', this.handleThemeEvent);
      document.addEventListener('shopify:block:select', this.handleThemeEvent);

      this.draw();
    }

    disconnectedCallback() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
      }
      document.removeEventListener('shopify:section:load', this.handleThemeEvent);
      document.removeEventListener('shopify:block:select', this.handleThemeEvent);
    }

    /* rAF here is a throttle for resize work only, not an animation loop. */
    handleResize() {
      if (this.frame) return;
      var rail = this;
      this.frame = requestAnimationFrame(function () {
        rail.frame = 0;
        rail.draw();
      });
    }

    handleThemeEvent(event) {
      var target = event && event.target;
      if (target && typeof target.contains === 'function' && !target.contains(this)) return;
      this.draw();
    }

    /* The host needs the same three values to size its own breathing room and
       to paint the thread. The stylesheet already sets them through :has(),
       this is only the backstop for engines without :has() support. */
    syncHost() {
      if (!this.host || this.host === this) return;
      this.host.style.setProperty('--rail-item-size', this.itemSize + 'px');
      if (this.dataset.fade) this.host.style.setProperty('--rail-fade', this.dataset.fade + 'px');
      if (this.dataset.lineColor) this.host.style.setProperty('--rail-line-color', this.dataset.lineColor);
    }

    buildPath(width) {
      /* The path runs past both side edges by a full item, so the jump from
         100% back to 0% always happens outside the clipped area. */
      var pad = Math.ceil(this.itemSize);
      var base = this.baseline;
      var start = -pad;
      var end = Math.round(width + pad);
      if (this.amplitude <= 0) {
        return 'M ' + start + ',' + base + ' L ' + end + ',' + base;
      }
      var span = end - start;
      var halves = Math.max(2, Math.round(span / 240) * 2);
      var step = span / halves;
      var d = 'M ' + start + ',' + base;
      for (var i = 0; i < halves; i++) {
        var x0 = start + step * i;
        var x1 = x0 + step;
        var y = i % 2 === 0 ? base - this.amplitude : base + this.amplitude;
        d +=
          ' C ' +
          (x0 + step / 3).toFixed(2) +
          ',' +
          y +
          ' ' +
          (x1 - step / 3).toFixed(2) +
          ',' +
          y +
          ' ' +
          x1.toFixed(2) +
          ',' +
          base;
      }
      return d;
    }

    draw() {
      var width = this.clientWidth || (this.host ? this.host.clientWidth : 0);
      if (!width) return;

      var d = this.buildPath(width);
      /* Attribute selector, not #id: a block id may start with a digit.
         Rewriting offset-path does not restart the running animation. */
      this.styleEl.textContent =
        '[id="' + this.id + '"] .marquee-rail__item { offset-path: path("' + d + '"); }';
      if (this.guide) this.guide.setAttribute('d', d);

      var length = pathLength(d, width + this.itemSize * 2);
      var speed = this.speed > 0 ? this.speed : 50;
      /* Changing the duration mid animation is a visible jump, so it is only
         updated when the path length really moved (5% threshold). */
      if (!this.measuredLength || Math.abs(length - this.measuredLength) / this.measuredLength > 0.05) {
        this.measuredLength = length;
        this.style.setProperty('--rail-duration', (length / speed).toFixed(3) + 's');
      }

      this.dataset.railReady = 'true';
    }
  }

  customElements.define('marquee-rail', MarqueeRail);
})();
